/* eslint-disable react/prop-types */

import anime from 'animejs';
import { cx } from 'emotion';
import { range } from 'ramda';
import React, {
  memo, useLayoutEffect, useRef, useMemo, useContext,
} from 'react';

import { BAC_COLORS } from '@ezugi/constants';

import Circle from './Circle';
import Dot from './Dot';
import Ring from './Ring';
import Slash from './Slash';
import {
  BEAD, BIG, BIG_EYE, SMALL, COCKROACH,
} from './constants';
import { fadeInClassName, pulseClassName } from './style';

const itemMap = {
  [BEAD]: Circle,
  [BIG]: Ring,
  [BIG_EYE]: Ring,
  [SMALL]: Circle,
  [COCKROACH]: Slash,
};

function getXOffset({ firstEmptyColumn, fixedColumns, zoomed }) {
  if (!zoomed || firstEmptyColumn < zoomed) return 0;

  return zoomed - fixedColumns;
}

function SvgRoadComponent({
  role,
  road: { rows, columns, items, firstEmptyColumn },
  columns: fixedColumns,
  rows: fixedRows,
  minColumns = 72,
  previews,
  gridCellSpan,
  zoomed,
  dispatch: _dispatch,
  withBackground,
  PreviewContext = React.createContext(),
  'data-e2e': dataE2e,
  gameId,
  ...props
}) {
  const svgRef = useRef(null);
  const zoomRef = useRef(null);
  const { preview: previewPlayer } = useContext(PreviewContext) || {};
  fixedColumns = fixedColumns || columns;
  fixedRows = fixedRows || rows;

  const minColumnsCount = minColumns < columns ? columns : minColumns;

  let rowsCount = fixedRows > rows ? fixedRows : rows;
  let columnsCount = fixedColumns > minColumnsCount ? fixedColumns : minColumnsCount;

  rowsCount += rowsCount % gridCellSpan;
  columnsCount += columnsCount % gridCellSpan;

  const Item = itemMap[role];

  const ITEM_TEXT_COLOR = useMemo(
    () => (role === BIG ? '#FFF' : undefined),
    [ role ],
  );

  const firstVisibleColumn = firstEmptyColumn > fixedColumns ? firstEmptyColumn - fixedColumns + 1 : 0;
  const { itemsToRender, itemsIndex } = useMemo(() => {
    const itemsPositions = [];
    const updatedItems = [];

    items
      .filter(({ coordinates }) => coordinates[1] >= firstVisibleColumn)
      .forEach((item) => {
        const { coordinates } = item;
        itemsPositions.push(coordinates[0] + (coordinates[1] - firstVisibleColumn) * fixedRows);
        updatedItems.push({ ...item, coordinates: [ coordinates[0], coordinates[1] - firstVisibleColumn ] });
      });

    return { itemsToRender: updatedItems, itemsIndex: itemsPositions };
  }, [ firstVisibleColumn, fixedRows, items ]);

  const placeholdersCoordinates = useMemo(() => {
    const coordinates = [];
    let forPreviewExcluded = false;

    range(0, fixedRows).forEach(
      (_y) => range(0, fixedColumns).forEach(
        (_x) => {
          const placeholderPosition = _y + _x * fixedRows;
          if (itemsIndex.includes(placeholderPosition)) {
            return;
          }
          if (!forPreviewExcluded && previewPlayer && previews[previewPlayer][role]) {
            const { coordinates: previewCoordinates } = previews[previewPlayer][role];
            const previewIndex = previewCoordinates[0] + (previewCoordinates[1] - firstVisibleColumn) * fixedRows;

            if (placeholderPosition === previewIndex) {
              forPreviewExcluded = true;
              return;
            }
          }
          coordinates.push([ _y, _x ]);
        },
      ),
    );
    return coordinates;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ firstEmptyColumn, fixedColumns, items, fixedRows, previewPlayer, previews, role, zoomed ]);

  const previewCoordinates = useMemo(() => {
    if (!previewPlayer || !previews[previewPlayer][role]) return null;
    const { coordinates } = previews[previewPlayer][role];
    const previewIndex = coordinates[0] + (coordinates[1] - firstVisibleColumn) * fixedRows;

    if (itemsIndex.includes(previewIndex)) return null;

    return [ coordinates[0], coordinates[1] - firstVisibleColumn ];
  }, [ firstVisibleColumn, fixedRows, itemsIndex, previewPlayer, previews, role ]);

  useLayoutEffect(() => {
    const xOffset = getXOffset({ firstEmptyColumn, fixedColumns, zoomed });

    anime({
      targets: zoomRef.current,
      x: xOffset + (xOffset % gridCellSpan),
      easing: 'linear',
      duration: 300,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ zoomed ]);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${fixedColumns || columnsCount} ${fixedRows || rowsCount}`}
      ref={svgRef}
      {...props}
    >
      <svg
        ref={zoomRef}
        viewBox={`0 0 ${columnsCount} ${rowsCount}`}
        x="0"
        y="0"
        width={columnsCount}
        height={rowsCount}
        data-e2e={dataE2e}
        data-hands-count={items.length}
      >
        {withBackground && (
          <rect
            x="0"
            y="0"
            fill={BAC_COLORS.GAME_BOARD_BG_COLOR_WITH_OPACITY}
            width={columnsCount}
            height={rowsCount}
          />
        )}
        {placeholdersCoordinates.map(([ _y, _x ]) => (
          <Dot key={`${_y}-${_x}`} coordinates={[ _y, _x ]} />
        ))}
        {itemsToRender.map((item) => (
          <Item key={item.roundId} {...item} className={fadeInClassName} gameId={gameId} textColor={ITEM_TEXT_COLOR} />
        ))}
        {previewPlayer && previews[previewPlayer][role] && (
          <Item
            {...previews[previewPlayer][role]}
            coordinates={previewCoordinates}
            className={cx(fadeInClassName, pulseClassName)}
            gameId={gameId}
          />
        )}
      </svg>
    </svg>
  );
}

export default memo(SvgRoadComponent);
